import React, { FC, useMemo } from 'react';
import { IAttachmentsForm } from '../use-attachments-form';
import { ProjectAttachment } from 'types/attachements';
import {
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  UncontrolledAccordion,
  UncontrolledTooltip,
} from 'reactstrap';
import {useTranslation} from 'react-i18next';
import messages from '../messages';
import {Trash2, X} from 'react-feather';
import TextInput from 'components/text-input';
import DragDropFile from 'components/dragndrop';
import {TemporaryFile} from 'types/temporary-file';
import DateInput from 'components/date-input';
import {ProjectAttachmentTypesEnum} from 'features/studio/projects/constants/project-attachments';
import Can from 'features/hylian-shield/can';
import AsyncItemPicker from 'components/async-item-picker';
import ProjectAttachmentViewer from './attachment-image-viewer';

export const AttachmentAccordion: FC<Props> = ({
  regroupedAttachments,
  validationErrors,
  subject,
  handleRemoveAttachment,
  handleChangeAttachment,
  handleAddAttachment,
}) => {
  const { t } = useTranslation();

  // collect attachments images where IMAGES_PHOTOS and HUB as types
  const hubImages = useMemo(() => {
    const allAttachments: (ProjectAttachment & { identifier: number })[] = [];

    regroupedAttachments.forEach(item => {
      if (item.type === ProjectAttachmentTypesEnum.IMAGES_PHOTOS ||
        item.type === ProjectAttachmentTypesEnum.HUB) {
        allAttachments.push(...item.data);
      }
    });

    return allAttachments;
  }, [regroupedAttachments]);

  return (
    <UncontrolledAccordion stayOpen>
      {regroupedAttachments.map((item, x) => (
        <AccordionItem key={x + item.type}>
          <AccordionHeader targetId={x.toString()}>
            <h5 color="light" className=" align-self-center">
              {t(`editProjectAttachmentSection.${item.type.toLowerCase()}`)}
              <Badge
                color={item.data.length > 0 ? 'success' : 'light'}
                className="mx-2"
              >
                {item.data.length}
              </Badge>
            </h5>
          </AccordionHeader>
          <AccordionBody accordionId={x.toString()}>
            <div>
              <Can action="edit" subject={subject}>
                <div className="d-flex justify-content-end">
                  <Button
                    className="me-2"
                    onClick={() => handleAddAttachment(item.type)}
                    color="important"
                  >
                    {t(...messages.addAttachment())}
                  </Button>
                </div>
                <hr />
              </Can>

              {/* Show image preview only for HUB type */}
              {item.type === ProjectAttachmentTypesEnum.HUB && (
                <ProjectAttachmentViewer attachments={hubImages} />
              )}

              {item.data.length > 0 ? (
                <>
                  {item.data.map((child, y) => (
                    <div key={x + y}>
                      {/* HUB */}
                      <div className="d-flex gap-2 mb-2">
                        {item.type ===
                          ProjectAttachmentTypesEnum.IMAGES_PHOTOS && (
                          <FormGroup
                            switch
                            className="d-flex justify-content-end gap-1"
                          >
                            <Input
                              checked={child.is_primary_image}
                              onChange={() => {
                                handleChangeAttachment(
                                  child.identifier,
                                  'is_primary_image',
                                  !child.is_primary_image
                                );
                              }}
                              type="switch"
                              role="switch"
                            />
                            <Label check>
                              {t(...messages.principalImage())}
                            </Label>
                          </FormGroup>
                        )}

                        {(item.type === ProjectAttachmentTypesEnum.HUB ||
                          item.type === ProjectAttachmentTypesEnum.PRESS) && (
                          <FormGroup
                            switch
                            className="d-flex justify-content-end gap-1"
                          >
                            <Input
                              checked={child.is_published}
                              onChange={() => {
                                handleChangeAttachment(
                                  child.identifier,
                                  'is_published',
                                  !child.is_published
                                );
                              }}
                              type="switch"
                              role="switch"
                            />
                            <Label check>{t(...messages.publish())}</Label>
                          </FormGroup>
                        )}
                      </div>
                      <Row className="align-items-center">
                        {item.type === ProjectAttachmentTypesEnum.HUB && (
                          <Col md={1}>
                            <TextInput
                              label={t(...messages.imageOrder())}
                              type="text"
                              value={child.order}
                              onChange={x =>
                                handleChangeAttachment(
                                  child.identifier,
                                  'order',
                                  x
                                )
                              }
                            />
                          </Col>
                        )}
                        <Col>
                          <TextInput
                            label={t(...messages.fileName())}
                            type="text"
                            value={child.name}
                            onChange={x =>
                              handleChangeAttachment(
                                child.identifier,
                                'name',
                                x
                              )
                            }
                          />
                        </Col>
                        {item.type === ProjectAttachmentTypesEnum.PRESS && (
                          <Col>
                            <AsyncItemPicker
                              endPoint="data/countries"
                              label={t(...messages.country())}
                              value={child.country}
                              errors={
                                validationErrors[
                                  `attachments.${child.identifier}.country`
                                ]
                              }
                              onChange={x =>
                                handleChangeAttachment(
                                  child.identifier,
                                  'country',
                                  x
                                )
                              }
                            />
                          </Col>
                        )}
                        {item.type === ProjectAttachmentTypesEnum.HUB && (
                          <Col md={5}>
                            <TextInput
                              label={t(...messages.legendAndCredits())}
                              type="text"
                              value={child.credits}
                              onChange={x =>
                                handleChangeAttachment(
                                  child.identifier,
                                  'credits',
                                  x
                                )
                              }
                            />
                          </Col>
                        )}
                      </Row>
                      <Row className="mt-2">
                        <Col className="align-items-center">
                          <div className="d-flex justify-content-between">
                            <label className="mb-0" htmlFor="">
                              {t(...messages.file())}
                            </label>
                            <Can action="edit" subject={subject}>
                              <a
                                className="primary"
                                onClick={() =>
                                  handleChangeAttachment(
                                    child.identifier,
                                    'file',
                                    ''
                                  )
                                }
                              >
                                <X size={18} />
                              </a>
                            </Can>
                          </div>
                          <DragDropFile
                            file={child?.file}
                            fileNameLength={60}
                            errors={
                              validationErrors[
                                `attachments.${child.identifier}.file_id`
                              ]
                            }
                            onChangeFile={(x: TemporaryFile) =>
                              handleChangeAttachment(
                                child.identifier,
                                'file',
                                x
                              )
                            }
                          />
                        </Col>
                        {item.type === ProjectAttachmentTypesEnum.PRESS && (
                          <Col className="align-items-center">
                            <div className="d-flex justify-content-between">
                              <label className="mb-0" htmlFor="">
                                {t(...messages.cover())}
                              </label>
                              <Can action="edit" subject={subject}>
                                <a
                                  className="primary"
                                  onClick={() =>
                                    handleChangeAttachment(
                                      child.identifier,
                                      'cover',
                                      ''
                                    )
                                  }
                                >
                                  <X size={18} />
                                </a>
                              </Can>
                            </div>
                            <DragDropFile
                              file={child?.cover?.file}
                              fileNameLength={60}
                              errors={
                                validationErrors[
                                  `attachments.${child.identifier}.cover`
                                ]
                              }
                              onChangeFile={(x: TemporaryFile) =>
                                handleChangeAttachment(
                                  child.identifier,
                                  'cover',
                                  x
                                )
                              }
                            />
                          </Col>
                        )}
                        <Col>
                          <div className="d-flex gap-1">
                            <div className="flex-grow-1">
                              <DateInput
                                value={child?.date}
                                label={t(...messages.date())}
                                onChange={x =>
                                  handleChangeAttachment(
                                    child.identifier,
                                    'date',
                                    x
                                  )
                                }
                              />
                            </div>
                            {/* <a
                              color="light"
                              className="btn btn-light d-inline-block align-self-end"
                              target="_blank"
                              rel="noreferrer"
                              href={child.original}
                            >
                              <Eye size={14} />
                            </a> */}
                            <Can action="edit" subject={subject}>
                              <Button
                                color="transparent"
                                className="d-inline-block align-self-end"
                                id={`tooltip-${(x + y).toString()}-remove`}
                                onDoubleClick={() =>
                                  handleRemoveAttachment(child.identifier)
                                }
                              >
                                <Trash2 className="text-danger" size={14} />
                              </Button>
                              <UncontrolledTooltip
                                placement="top"
                                target={`tooltip-${(x + y).toString()}-remove`}
                              >
                                {t('common.doubleClickToRemove')}
                              </UncontrolledTooltip>
                            </Can>
                          </div>
                        </Col>
                      </Row>
                      <hr
                        className="mt-4"
                        style={{
                          display: y < item.data.length - 1 ? 'block' : 'none',
                        }}
                      ></hr>
                    </div>
                  ))}
                </>
              ) : (
                <p className="text-center">{t('common.noData')}</p>
              )}
            </div>
          </AccordionBody>
        </AccordionItem>
      ))}
    </UncontrolledAccordion>
  );
};

type Props = IAttachmentsForm;
